import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "wouter";

import { api } from "@/api";
import { Button, money, Skeleton, StatusBadge } from "@/components/ui";
import { useSession } from "@/session/SessionProvider";

// Matches apps/betting/views.py:_coupon_payload and the existing player
// history DTO. This is all account coupons, not the per-race HUD projection.
export type MarketCouponPage = {
  coupons: Array<{
    couponId: string;
    type: string;
    systemK: number | null;
    stakeCents: number;
    potentialCents: number;
    status: string;
    walletState: string;
    result: string | null;
    returnedCents: number;
    placedAt: string;
    settledAt: string | null;
    legs: Array<{
      raceId: string;
      betType: string;
      sel: number[];
      oddsH: number;
      grade: string;
    }>;
  }>;
  nextCursor: string | null;
};

export function MarketMyBets() {
  const { session, requestLogin } = useSession();
  if (!session) {
    return (
      <div className="market-panel__state">
        <strong>Your bets, in one place</strong>
        <p>Sign in to see your account bets, including multis.</p>
        <Button onClick={requestLogin}>Sign in</Button>
      </div>
    );
  }
  return (
    <AccountBets
      key={session.playerId}
      playerId={session.playerId}
      currency={session.currency}
    />
  );
}

function AccountBets({
  playerId,
  currency,
}: {
  playerId: number;
  currency: string;
}) {
  const bets = useInfiniteQuery({
    // A distinct suffix avoids sharing InfiniteData with PlayerPage/prefetch's
    // single-page cache while retaining all existing ["bets"] invalidations.
    queryKey: ["bets", playerId, "history"],
    queryFn: ({ pageParam }) =>
      api.myCoupons(pageParam) as Promise<MarketCouponPage>,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 30_000,
  });
  if (bets.isPending)
    return (
      <div className="market-panel__state" aria-label="Loading your bets">
        <Skeleton rows={4} />
      </div>
    );
  const coupons = bets.data?.pages.flatMap((page) => page.coupons) ?? [];
  const retry = () => {
    if (bets.isFetchNextPageError) void bets.fetchNextPage();
    else void bets.refetch();
  };
  return (
    <div className="market-panel__history">
      <p className="market-panel__hint">
        All account bets, including multis across races.
      </p>
      {bets.error ? (
        <div
          className="market-panel__state market-panel__state--compact"
          role="alert"
        >
          <strong>Bet history temporarily unavailable</strong>
          <p>
            {coupons.length
              ? "Your loaded bets are still shown below."
              : "Please retry to load your account bets."}
          </p>
          <Button
            variant="secondary"
            disabled={bets.isFetching}
            onClick={retry}
          >
            Retry bets
          </Button>
        </div>
      ) : null}
      {!bets.error && coupons.length === 0 ? (
        <div className="market-panel__state" role="status">
          <strong>No wagers yet</strong>
          <p>Your placed bets will appear here.</p>
        </div>
      ) : null}
      <ol className="market-panel__coupons">
        {coupons.map((coupon) => (
          <li className="market-panel__coupon" key={coupon.couponId}>
            <header>
              <strong>
                {coupon.type === "combined"
                  ? "Multi"
                  : coupon.type === "system"
                    ? `System ${coupon.systemK ?? ""}`.trim()
                    : "Single"}
              </strong>
              <StatusBadge status={coupon.status}>
                {coupon.status.replaceAll("_", " ")}
              </StatusBadge>
            </header>
            <time dateTime={coupon.placedAt}>
              {new Date(coupon.placedAt).toLocaleString([], {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </time>
            <ul className="market-panel__coupon-legs">
              {coupon.legs.map((leg, index) => (
                <li key={`${leg.raceId}-${index}`}>
                  <strong>
                    {leg.betType.toUpperCase()}{" "}
                    {leg.sel
                      .map((gate) => gate + 1)
                      .join(leg.betType === "quinella" ? " + " : " → ")}
                  </strong>
                  <small>
                    <Link href={`/app/?watch=${leg.raceId}`}>
                      View race {index + 1}
                    </Link>{" "}
                    · {leg.grade}
                  </small>
                </li>
              ))}
            </ul>
            <dl className="market-panel__stats">
              <div>
                <dt>Stake</dt>
                <dd>{money(coupon.stakeCents, currency)}</dd>
              </div>
              <div>
                <dt>Returned</dt>
                <dd>{money(coupon.returnedCents, currency)}</dd>
              </div>
              {coupon.status === "pending" ||
              coupon.status === "open" ||
              coupon.status === "settling" ? (
                <div>
                  <dt>Potential return</dt>
                  <dd>{money(coupon.potentialCents, currency)}</dd>
                </div>
              ) : null}
              <div>
                <dt>Wallet</dt>
                <dd>{coupon.walletState}</dd>
              </div>
              {coupon.result ? (
                <div>
                  <dt>Result</dt>
                  <dd>{coupon.result.replaceAll("_", " ")}</dd>
                </div>
              ) : null}
            </dl>
          </li>
        ))}
      </ol>
      {bets.hasNextPage ? (
        <Button
          variant="secondary"
          disabled={bets.isFetching}
          onClick={() => void bets.fetchNextPage()}
        >
          {bets.isFetchingNextPage ? "Loading…" : "Load more bets"}
        </Button>
      ) : null}
    </div>
  );
}
